import { Power2 } from 'gsap';
import * as PIXI from 'pixi.js';

import Text from '../../graphics/Text';
import DraggablePreset from './DraggablePreset';
import Rectangle from '../../graphics/Rectangle';
import { NONE } from '../../constants/DropShadow';
import GraphicMask from '../../graphics/GraphicMask';
import {
  getAlignX,
  getCenterX,
  getCenterY,
  getXAccountingForAnchor,
} from '../../utils/CoordinateCalculator';

const MAX_DURATION = 3;

export default class Highlighter extends DraggablePreset {
  constructor(app) {
    super(app);

    this.highlightBoxes = [];
    this.texts = [];
    this.textMask = new GraphicMask();
    this.backgroundBox = new Rectangle();

    this.zoomableContainer = new PIXI.Container();
    this.zoomedGraphics = new PIXI.Graphics();

    this.zoomableContainer.addChild(this.zoomedGraphics, this.textMask.mask);
    this.container.addChild(this.zoomableContainer);
  }

  update(model) {
    model = {
      ...model,
      dropShadow: NONE, // disable drop shadow for this preset
      anchor: { x: 0.5, y: 0.5 }, // center anchor because it should grow outwardly. NOTE: this means scaling happens from center.
    };
    super.update(model);

    if (this.texts.length) {
      this.zoomableContainer.removeChild(...this.texts);
    }

    if (!this.numTextLines) {
      this.highlightBoxes = [];
      this.texts = [];
      this.backgroundBox.height = 0;
      return;
    }

    const animationDuration = Math.min(this.duration, MAX_DURATION) / 4;

    this.texts = this.textLines.map((line) => {
      const text = new Text(this.app, line);
      text.updateStyle(model);
      text.mask = this.textMask.mask;
      return text;
    });
    this.zoomableContainer.addChild(...this.texts);
    const boxPadding = this.texts[0].fontSize * 0.2;

    const maxTextWidth = Math.max(
      ...this.texts.map((text) => text.lineControls.width)
    );
    const stackedTextHeight = this.texts.reduce(
      (accumulatedHeight, text) => accumulatedHeight + text.lineControls.height,
      0
    );
    const shiftX = this.numTextLines > 1 ? maxTextWidth / 4 : 0;
    const shiftedHeight = this.texts.reduce(
      (accumulatedHeight, text) =>
        accumulatedHeight + text.innerControls.height + boxPadding * 2,
      0
    );
    this.setDraggableRoot(
      shiftedHeight,
      maxTextWidth + 2 * shiftX + boxPadding * 2
    );

    const stackedBounds = {
      width: maxTextWidth,
      height: stackedTextHeight,
    };
    stackedBounds.x = getCenterX(stackedBounds, this.root);
    stackedBounds.y = getCenterY(stackedBounds, this.root);

    let fromY = stackedBounds.y - boxPadding;
    let toY = this.root.y;

    this.highlightBoxes = this.texts.map((text, index) => {
      text.lineControls.x = getAlignX(text.lineControls, stackedBounds);
      text.lineControls.y = stackedBounds.y + index * text.lineControls.height;

      const boxPadding = text.fontSize * 0.2;

      const from = {
        x: stackedBounds.x - boxPadding,
        y: fromY,
        width: maxTextWidth + boxPadding * 2,
        height: text.lineControls.height,
      };
      if (index === 0) {
        from.height += boxPadding;
      }
      if (index === this.numTextLines - 1) {
        from.height += boxPadding;
      }
      fromY += from.height;

      let alignRight = this.align === 'right';
      if (index % 2 === 1) {
        alignRight = !alignRight;
      }
      const alignment = alignRight ? 'right' : 'left';
      const to = {
        width: maxTextWidth + boxPadding * 2,
        height: text.innerControls.height + boxPadding * 2,
        y: toY,
      };
      to.x = getAlignX(to, this.root, alignment, 0, this.app.screen.height);
      toY += to.height;

      const box = new Rectangle();
      box.color = this.secondaryColor;
      box.x = from.x;
      box.y = from.y;
      box.width = from.width;
      box.height = 0;
      this.animate(box)
        .to(
          {
            height: from.height,
            duration: 0,
          },
          animationDuration
        )
        .to(
          {
            ...to,
            duration: animationDuration,
            ease: Power2.easeInOut,
          },
          animationDuration
        )
        .reverseOut();

      this.animate(text.innerControls)
        .to(
          {
            x: getCenterX(text.innerControls, to),
            y: getCenterY(text.innerControls, to),
            duration: animationDuration,
            ease: Power2.easeInOut,
          },
          animationDuration
        )
        .reverseOut();

      return box;
    });

    this.backgroundBox.color = this.secondaryColor;
    this.backgroundBox.width = stackedBounds.width + boxPadding * 2;
    this.backgroundBox.height = 0;
    this.backgroundBox.x = stackedBounds.x - boxPadding;
    this.backgroundBox.y = stackedBounds.y - boxPadding;
    this.animate(this.backgroundBox)
      .to({
        height: stackedBounds.height + boxPadding * 2,
        duration: animationDuration,
        ease: Power2.easeInOut,
      })
      .to(
        {
          height: 0,
          duration: 0,
        },
        animationDuration
      )
      .reverseOut();

    // reveal text as background box opens
    this.textMask.width = stackedBounds.width + boxPadding * 2;
    this.textMask.height = 0;
    this.textMask.x = stackedBounds.x - boxPadding;
    this.textMask.y = stackedBounds.y - boxPadding;

    this.animate(this.textMask)
      .to({
        height: stackedBounds.height + boxPadding * 2,
        duration: animationDuration,
        ease: Power2.easeInOut,
      })
      .to(
        {
          x: getXAccountingForAnchor(this.root),
          y: this.root.y,
          width: this.root.width,
          height: this.root.height,
          duration: animationDuration,
        },
        animationDuration
      )
      .reverseOut();

    // add zoom effect
    this.zoomableContainer.scale.x = 0.8;
    this.zoomableContainer.scale.y = 0.8;
    this.animate(this.zoomableContainer.scale)
      .to(
        {
          x: 1,
          y: 1,
          duration: animationDuration,
          ease: Power2.easeInOut,
        },
        animationDuration
      )
      .reverseOut();
  }

  render() {
    super.render();
    this.zoomedGraphics.clear();
    this.highlightBoxes.forEach((box) => box.draw(this.zoomedGraphics));
    this.backgroundBox.draw(this.zoomedGraphics);
    this.textMask.render();
  }
}
